import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { superAdminLogin } from '../../services/api';
import PasswordInput from '../common/PasswordInput';

/** Central (TVG / Ekta) shared-secret login for the Mission Control master
 *  dashboard — separate from the facilitator /monitor login. */
const SuperAdminLogin: React.FC = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { token } = await superAdminLogin(username.trim(), password);
      localStorage.setItem('superAdminToken', token);
      navigate('/super-admin', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F5F4FA] px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-3xl border border-[#E9E6F4] bg-white p-8 shadow-lg shadow-slate-200/60"
      >
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 text-xl text-white">
            ✦
          </div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900">Mission Control</h1>
          <p className="mt-1 text-sm text-slate-500">Central leadership sign-in</p>
        </div>

        <label className="mb-1 block text-xs font-semibold text-slate-500">Username</label>
        <input
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoFocus
          className="mb-4 w-full rounded-xl border border-[#E9E6F4] px-3 py-2.5 text-sm focus:border-violet-400 focus:outline-none"
        />

        <label className="mb-1 block text-xs font-semibold text-slate-500">Password</label>
        <PasswordInput
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          wrapperClassName="mb-4"
          className="w-full rounded-xl border border-[#E9E6F4] px-3 py-2.5 text-sm focus:border-violet-400 focus:outline-none"
        />

        {error ? <p className="mb-3 text-sm text-rose-600">{error}</p> : null}

        <button
          type="submit"
          disabled={busy || !username.trim() || !password}
          className="w-full rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
};

export default SuperAdminLogin;
